import { Grid } from '@mui/material';
import { compareDates, dateToFormatCreateRouteDrivers, getWeekday } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalLoader as Loader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { PlusIcon } from '../../../../../assets/svg/PlusIcon';
import { DRIVERS_LAYOUT_ID } from '../../../../../components/DriversLayout';
import { SelectPill } from '../../../../../components/SelectPill';
import { SlidingMenu } from '../../../../../components/SlidingMenu';
import { StyledFooterButton } from '../../../../../components/StyledFooterButton';
import * as routing from '../../../../../constants/Routing';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveLanguage } from '../../../../../hooks/translations/useRetrieveActiveLanguage';
import InputText from '../../../../../react-components/InputText';
import { Text } from '../../../../../react-components/Text';
import { CreateRouteFieldContainer } from '../../../routes-list/route-creation/CreateRouteFieldContainer';
import { RoutalDatePicker } from '../../../routes-list/route-creation/RoutalDatePicker';
import { useRouteContext } from '../../context/context';
import { useDuplicateRoute } from '../../hooks/useDuplicateRoute';

interface RouteDuplicationProps {
  route_id: string;
  isOpen: boolean;
  onClose: () => void;
  zIndex?: number;
  portalElementId?: string;
}

export const RouteDuplication = ({
  route_id,
  isOpen,
  onClose,
  zIndex = 1005,
  portalElementId = DRIVERS_LAYOUT_ID,
}: RouteDuplicationProps) => {
  const navigate = useNavigate();
  const { routeState } = useRouteContext();
  const { activeLanguage } = useRetrieveActiveLanguage();
  const { run: duplicateRoute, success, isLoading } = useDuplicateRoute();
  const [executionDate, setExecutionDate] = useState<Date>(new Date());
  const [planName, setPlanName] = useState<string>(
    getWeekday({
      date: new Date(),
      language: activeLanguage,
    })
  );
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  useEffect(() => {
    if (success && routeState.route?.id) {
      navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, routeState.route?.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  //Selected date logic
  const todayDate = new Date();
  const tomorrowDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000);
  const [lastSelectedDate, setLastSelectedDate] = useState<Date | undefined>(undefined);

  const content = (
    <>
      <RoutalDatePicker
        value={new Date()}
        isOpen={openDatePicker}
        onSelect={(date: Date) => {
          setExecutionDate(date);
          setLastSelectedDate(date);
          setOpenDatePicker(false);
        }}
        onCancel={() => setOpenDatePicker(false)}
        zIndex={zIndex + 1}
      />
      <SlidingMenu
        id="route-duplication"
        isOpen={isOpen}
        direction="up"
        onClose={onClose}
        slideStyles={{ zIndex, top: 0, left: 0 }}
        childrenStyles={{ backgroundColor: RoutalPalette.white, padding: 0 }}
        headerStyles={{ backgroundColor: RoutalPalette.white }}
        headerContent={
          <Grid
            style={{
              display: `flex`,
              flexDirection: `row`,
              flexWrap: `nowrap`,
              width: `100%`,
              height: `70px`, // Same as SlidingMenu
              justifyContent: `space-between`,
              alignItems: `center`,
            }}
          >
            <Text variant="h6" isBold intlId="drivers.route.creation.title" />
          </Grid>
        }
        footerContent={
          <Grid
            style={{
              width: `100%`,
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              height: `70px`,
              zIndex,
              backgroundColor: RoutalPalette.white,
              padding: `0 14px`,
              borderTop: `1px solid ${RoutalPalette.neutral10}`,
            }}
          >
            <StyledFooterButton
              onClick={() => {
                duplicateRoute({ label: planName, execution_date: executionDate, route_id });
              }}
              disabled={isLoading || success}
            >
              <PlusIcon color={RoutalPalette.white} />
              <HSpacer small />
              {intl.formatMessage({ id: `drivers.route.duplicate` })}
            </StyledFooterButton>
          </Grid>
        }
      >
        {isLoading ? (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{
              height: `100%`,
              overflow: `hidden`,
            }}
          >
            <Loader />
          </Grid>
        ) : (
          <Grid
            style={{
              height: `100%`,
              width: `100%`,
              background: RoutalPalette.white,
              padding: `24px 20px 24px 20px`,
            }}
          >
            <Grid
              style={{
                height: `100%`,
                width: `100%`,
                background: RoutalPalette.white,
              }}
            >
              <Grid>
                <CreateRouteFieldContainer key={`route-creation-name`} textId={`drivers.route.creation.name`}>
                  <InputText
                    placeholderIntlId="drivers.route.creation.name_ph"
                    fieldValue={planName || ``}
                    onChange={(value) => setPlanName(value)}
                    variant="outlined"
                    inputContainerStyles={{ width: `100%`, height: `40px` }}
                    inputStyles={{
                      width: `100%`,
                      borderColor: RoutalPalette.grey7,
                      color: planName ? RoutalPalette.text : undefined,
                    }}
                  />
                </CreateRouteFieldContainer>
                <VSpacer medium />
                <CreateRouteFieldContainer key={`route-creation-name`} textId={`drivers.route.creation.day_select`}>
                  <Grid item style={{ display: `contents`, width: `100%` }}>
                    <SelectPill
                      selected={compareDates(todayDate, executionDate)}
                      onClick={() => setExecutionDate(todayDate)}
                    >
                      <Text intlId="drivers.route.creation.today" />
                      <HSpacer small />
                      <Text variant="label">{dateToFormatCreateRouteDrivers(todayDate, activeLanguage)}</Text>
                    </SelectPill>
                    <VSpacer small />
                    <SelectPill
                      selected={compareDates(tomorrowDate, executionDate)}
                      onClick={() => setExecutionDate(tomorrowDate)}
                    >
                      <Text intlId="drivers.route.creation.tomorrow" />
                      <HSpacer small />
                      <Text variant="label">{dateToFormatCreateRouteDrivers(tomorrowDate, activeLanguage)}</Text>
                    </SelectPill>
                    <VSpacer small />
                    {lastSelectedDate && (
                      <>
                        <SelectPill
                          selected={compareDates(lastSelectedDate, executionDate)}
                          onClick={() => setExecutionDate(lastSelectedDate)}
                        >
                          <Text>{lastSelectedDate.toLocaleDateString()}</Text>
                          <HSpacer small />
                          <Text variant="label">{dateToFormatCreateRouteDrivers(tomorrowDate, activeLanguage)}</Text>
                        </SelectPill>
                        <VSpacer small />
                      </>
                    )}
                    <SelectPill radialIcon={false} onClick={() => setOpenDatePicker(true)}>
                      <Text intlId="drivers.route.creation.day_select" />
                    </SelectPill>
                  </Grid>
                </CreateRouteFieldContainer>
                <VSpacer medium />
              </Grid>
            </Grid>
          </Grid>
        )}
      </SlidingMenu>
    </>
  );
  const element = document.getElementById(portalElementId ?? ``);
  if (element) {
    return createPortal(content, element as HTMLElement);
  }
  return content;
};
